import type React from 'react';

import type { FireAnalyticsEvent } from '../../types';

type MoreContext = Record<string, unknown>;

const baseVitalStatsEvent = ({
	searchSessionId,
	taskId,
	task,
	action,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	action: 'taskStart' | 'taskSuccess' | 'taskFail' | 'taskAbort';
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return {
		action,
		actionSubject: 'ui',
		eventType: 'operational' as const,
		attributes: {
			additionalAttributes: {
				task,
				taskId,
				hostname: window.location.hostname,
				searchSessionId,
				...moreContext,
			},
		},
	};
};

/**
 * State machine type for tracking network VitalStats task lifecycle.
 * Used across main.tsx, query-plan, search-results, and error components.
 *
 * State transitions:
 * - idle -> started (on taskStart)
 * - started -> completed (on taskSuccess)
 * - started -> failed (on taskFail)
 * - started -> aborted (on taskAbort - superseded or unmounted)
 */
export type NetworkTaskState = {
	taskId: string | null;
	uxTaskId: string | null;
	cacheAvailable: boolean;
	state: 'idle' | 'started' | 'completed' | 'failed' | 'aborted';
};

export type FullPageSearchNetworkRefs = React.MutableRefObject<{
	taskId: string | null;
	startAt: number;
	state: NetworkTaskState;
}>;

export const TASK_NAMES = {
	SEARCH_PAGE_FIRST_PARTY_CONFIG_UX: 'search-page/first-party-config-ux',
	SEARCH_PAGE_FIRST_PARTY_CONFIG_NETWORK: 'search-page/first-party-config-network',
	SEARCH_PAGE_THIRD_PARTY_CONFIG_UX: 'search-page/third-party-config-ux',
	SEARCH_PAGE_THIRD_PARTY_CONFIG_NETWORK: 'search-page/third-party-config-network',
	SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_UX: 'search-page/full-page-search-results-ux',
	SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK: 'search-page/full-page-search-results-network',
	QUICK_FIND_RECENT_ACTIVITIES_UX: 'quick-find/recent-activities-ux',
	QUICK_FIND_PRE_QUERY_RESULTS: 'quick-find/prequery-search-results-ux',
	QUICK_FIND_POST_QUERY_UX: 'quick-find/postquery-search-results-ux',
	QUICK_FIND_CONFLUENCE_ENTITIES_NETWORK: 'quick-find/confluence-entities-network',
	QUICK_FIND_CONFLUENCE_SPACES_NETWORK: 'quick-find/confluence-spaces-network',
	QUICK_FIND_JIRA_ISSUES_AND_TYPES_NETWORK: 'quick-find/jira-issues-and-types-network',
	QUICK_FIND_JIRA_ENTITIES_NETWORK: 'quick-find/jira-entities-network',
	QUICK_FIND_ATLAS_ENTITIES_NETWORK: 'quick-find/atlas-entities-network',
	QUICK_FIND_MERCURY_ENTITIES_NETWORK: 'quick-find/mercury-entities-network',
	QUICK_FIND_TALENT_ENTITIES_NETWORK: 'quick-find/talent-entities-network',
	QUICK_FIND_ASSETS_ENTITIES_NETWORK: 'quick-find/assets-entities-network',
	QUICK_FIND_INTENT_DETECTION_NETWORK: 'quick-find/intent-detection-network',
} as const;

type TaskNameValue = (typeof TASK_NAMES)[keyof typeof TASK_NAMES];

export const onTaskStart = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskStart', moreContext });
};

export const onTaskSuccess = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskSuccess', moreContext });
};

export const onTaskFail = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskFail', moreContext });
};

export const onTaskAbort = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: TaskNameValue;
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskAbort', moreContext });
};

/**
 * Creates Vital Stats events for Quick Find by converting analytics payloads
 * from FPS format to Quick Find's expected structure.
 * This is to ensure consistency of the payloads (all atributes are at the top level).
 *
 * @param event normally an output of either onTaskStart, onTaskSuccess, onTaskFail, or onTaskAbort
 */
export const remapVitalStatsEventForQuickFind = (event: FireAnalyticsEvent): FireAnalyticsEvent => {
	return {
		...event,
		attributes: {
			...event.attributes.additionalAttributes,
		},
	};
};
